/**
 * Vista GLOBAL "Evaluaciones" del terapeuta: la evaluación es la unidad de
 * trabajo diaria, así que tiene entrada propia (no ficha-por-ficha). Lista
 * cross-paciente (propios + vinculados; la RLS filtra en el backend) en orden
 * cronológico desc, con contadores accionables, filtros persistentes y navegación
 * por estado. El nombre del niño (identity) y la firma M11 (clinical-review) se
 * hidratan en bloque desde el cliente (sin N+1), respetando la regla de oro.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { bateriaApi, childrenApi, clinicalApi } from "../../api/endpoints.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Field, Select, InfoBanner, ErrorBanner } from "../../components/Form.js";
import { Loader } from "../../components/Loader.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import type { BateriaGlobalResumen, EstadoBateria, FiltrosBateriaGlobal, Nino } from "../../api/types.js";
import s from "./EvaluationsPage.module.css";

const PAGE = 50;
const STORAGE = "vc.evaluaciones.filtros";

/** Filtros de la vista (sin paginación; el offset vive aparte). */
type Filtros = Omit<FiltrosBateriaGlobal, "limit" | "offset">;

function leerFiltros(): Filtros {
  try {
    const raw = sessionStorage.getItem(STORAGE);
    return raw ? (JSON.parse(raw) as Filtros) : {};
  } catch {
    return {};
  }
}

/** ISO YYYY-MM-DD de hace N días (para el chip "recientes"). */
function haceDias(n: number): string {
  return new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
}

function edadTexto(meses: number): string {
  const a = Math.floor(meses / 12);
  const m = meses % 12;
  return m ? `${a}a ${m}m` : `${a}a`;
}

const PERFIL_CORTO: Record<string, string> = {
  DESARROLLO_TIPICO: "Típico",
  DESARROLLO_TIPICO_CON_VIGILANCIA: "Típico (vigilancia)",
  PERFIL_FONETICO: "Fonético",
  PERFIL_FONOLOGICO: "Fonológico",
  PERFIL_MIXTO: "Mixto",
  NO_CONCLUYENTE: "No concluyente",
};

const ESTADO_BADGE: Record<EstadoBateria, { label: string; tono: string }> = {
  creada: { label: "Creada", tono: s.gris! },
  mic_check: { label: "Prueba de micrófono", tono: s.gris! },
  en_progreso: { label: "En progreso", tono: s.azul! },
  completada: { label: "Pendiente de procesar", tono: s.ambar! },
  procesando: { label: "Procesando", tono: s.ambar! },
  procesada: { label: "Procesada", tono: s.verde! },
  inconcluyente: { label: "Inconcluyente", tono: s.coral! },
  suspendida: { label: "Suspendida", tono: s.gris! },
  cancelada: { label: "Cancelada", tono: s.gris! },
};

const ESTADOS: EstadoBateria[] = [
  "creada",
  "mic_check",
  "en_progreso",
  "completada",
  "procesando",
  "procesada",
  "inconcluyente",
  "suspendida",
  "cancelada",
];

/** Destino según estado: procesada/inconcluyente → mesa de revisión (con motivo);
 *  el resto → ficha del paciente (donde vive el botón "Procesar" del #56). */
function destino(b: BateriaGlobalResumen): string {
  if (b.estado === "procesada" || b.estado === "inconcluyente") return `/terapeuta/revision/${b.id}`;
  return `/terapeuta/paciente/${b.childId}`;
}

export function EvaluationsPage() {
  const [filtros, setFiltros] = useState<Filtros>(leerFiltros);
  const [offset, setOffset] = useState(0);
  const [abiertoFiltros, setAbiertoFiltros] = useState(false);
  const [buscarNino, setBuscarNino] = useState("");
  const [procesandoId, setProcesandoId] = useState<string | null>(null);
  const [procError, setProcError] = useState<string | null>(null);

  // Persistimos el último filtro en la sesión.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE, JSON.stringify(filtros));
    } catch {
      /* almacenamiento no disponible: seguimos sin persistir */
    }
  }, [filtros]);

  const filtrosKey = JSON.stringify(filtros);
  const lista = useQuery(
    () => bateriaApi.listarGlobal({ ...filtros, limit: PAGE, offset }),
    [filtrosKey, offset],
  );
  const stats = useQuery(() => bateriaApi.statsGlobal(), []);
  const ninos = useQuery(() => childrenApi.listar(), []);

  const items = useMemo(() => lista.data?.items ?? [], [lista.data]);
  const total = lista.data?.total ?? 0;
  const idsKey = items.map((b) => b.id).join(",");

  // Hidratación en bloque de la firma M11 (evita N+1).
  const firma = useQuery(
    () =>
      items.length > 0
        ? clinicalApi.estadoValidaciones(items.map((b) => b.id))
        : Promise.resolve([]),
    [idsKey],
  );

  const childMap = useMemo(
    () => new Map<string, Nino>((ninos.data ?? []).map((n) => [n.id, n])),
    [ninos.data],
  );
  const firmaMap = useMemo(
    () => new Map((firma.data ?? []).map((f) => [f.bateriaId, f])),
    [firma.data],
  );

  function aplicar(f: Filtros) {
    setFiltros(f);
    setOffset(0);
  }
  // Fusiona un parche; un valor undefined/"" ELIMINA la clave (exactOptionalPropertyTypes).
  function parche(p: { [K in keyof Filtros]?: Filtros[K] | undefined }) {
    const next: Filtros = { ...filtros };
    for (const k of Object.keys(p) as (keyof Filtros)[]) {
      const v = p[k];
      if (v === undefined || v === "") delete next[k];
      else (next as Record<string, unknown>)[k] = v;
    }
    aplicar(next);
  }

  // Procesar una batería 'completada' desde aquí (consume 1 crédito, idempotente).
  async function procesar(id: string) {
    setProcError(null);
    setProcesandoId(id);
    try {
      await bateriaApi.procesar(id);
      lista.reload();
      stats.reload();
    } catch (e) {
      setProcError(mensajeDeError(e));
    } finally {
      setProcesandoId(null);
    }
  }

  const ninosOrdenados = useMemo(
    () => [...(ninos.data ?? [])].sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [ninos.data],
  );
  const ninosFiltrados = buscarNino
    ? ninosOrdenados.filter((n) => n.nombre.toLowerCase().includes(buscarNino.toLowerCase()))
    : ninosOrdenados;

  const hayFiltro =
    !!filtros.childId ||
    !!filtros.estado ||
    !!filtros.modo ||
    !!filtros.conFlags ||
    !!filtros.desde ||
    !!filtros.hasta;

  const pagina = Math.floor(offset / PAGE) + 1;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE));

  return (
    <>
      <ScreenHeader title="Evaluaciones" />
      <Content>

        {/* ---- contadores accionables (cada chip aplica su filtro) ---- */}
        <div className={s.chips}>
          <button
            type="button"
            className={s.chip}
            aria-pressed={filtros.estado === "completada"}
            onClick={() => aplicar({ estado: "completada" })}
          >
            <b>{stats.data?.porProcesar ?? "—"}</b>
            <span>Por procesar</span>
          </button>
          <button
            type="button"
            className={s.chip}
            aria-pressed={filtros.estado === "procesada" && !!filtros.conFlags}
            onClick={() => aplicar({ estado: "procesada", conFlags: true })}
          >
            <b>{stats.data?.conMarcas ?? "—"}</b>
            <span>Con marcas por revisar</span>
          </button>
          <button
            type="button"
            className={s.chip}
            aria-pressed={filtros.estado === "procesada" && !!filtros.desde}
            onClick={() => aplicar({ estado: "procesada", desde: haceDias(7) })}
          >
            <b>{stats.data?.procesadasRecientes ?? "—"}</b>
            <span>Procesadas (7 días)</span>
          </button>
        </div>

        {/* ---- filtros (colapsables en móvil) ---- */}
        <Card>
          <div className={s.filtrosHead}>
            <h2 style={{ margin: 0, fontSize: 16 }}>Filtros</h2>
            <div style={{ display: "flex", gap: 8 }}>
              {hayFiltro ? (
                <Button variant="ghost" onClick={() => aplicar({})}>
                  Limpiar
                </Button>
              ) : null}
              <Button variant="ghost" onClick={() => setAbiertoFiltros((v) => !v)}>
                {abiertoFiltros ? "Ocultar" : "Mostrar"}
              </Button>
            </div>
          </div>
          {abiertoFiltros ? (
            <div className={s.filtros}>
              <Field label="Paciente">
                <input
                  className={s.input}
                  placeholder="Buscar por nombre…"
                  value={buscarNino}
                  onChange={(e) => setBuscarNino(e.target.value)}
                />
                <Select
                  value={filtros.childId ?? ""}
                  onChange={(e) => parche({ childId: e.target.value || undefined })}
                >
                  <option value="">Todos los pacientes</option>
                  {ninosFiltrados.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.nombre}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Estado">
                <Select
                  value={filtros.estado ?? ""}
                  onChange={(e) => parche({ estado: (e.target.value || undefined) as EstadoBateria | undefined })}
                >
                  <option value="">Todos</option>
                  {ESTADOS.map((es) => (
                    <option key={es} value={es}>
                      {ESTADO_BADGE[es].label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Modo">
                <Select
                  value={filtros.modo ?? ""}
                  onChange={(e) =>
                    parche({ modo: (e.target.value || undefined) as "prueba" | "normal" | undefined })
                  }
                >
                  <option value="">Todos</option>
                  <option value="normal">Normal</option>
                  <option value="prueba">QA (prueba)</option>
                </Select>
              </Field>
              <Field label="Desde">
                <input
                  type="date"
                  className={s.input}
                  value={filtros.desde ?? ""}
                  onChange={(e) => parche({ desde: e.target.value || undefined })}
                />
              </Field>
              <Field label="Hasta">
                <input
                  type="date"
                  className={s.input}
                  value={filtros.hasta ?? ""}
                  onChange={(e) => parche({ hasta: e.target.value || undefined })}
                />
              </Field>
              <label className={s.check}>
                <input
                  type="checkbox"
                  checked={!!filtros.conFlags}
                  onChange={(e) => parche({ conFlags: e.target.checked || undefined })}
                />
                Solo con marcas por revisar
              </label>
            </div>
          ) : null}
        </Card>

        {/* ---- lista ---- */}
        <Card>
          <div className={s.listaHead}>
            <h2 style={{ margin: 0, fontSize: 16 }}>
              {total} {total === 1 ? "evaluación" : "evaluaciones"}
            </h2>
          </div>
          {procError ? <ErrorBanner message={procError} /> : null}
          {lista.loading ? <Loader label="Cargando…" /> : null}
          {lista.error ? (
            <>
              <ErrorBanner message={lista.error} />
              <Button variant="ghost" onClick={lista.reload}>
                Reintentar
              </Button>
            </>
          ) : null}
          {!lista.loading && !lista.error && items.length === 0 ? (
            <InfoBanner>No hay evaluaciones que coincidan con el filtro.</InfoBanner>
          ) : null}

          <ul className={s.rows}>
            {items.map((b) => {
              const nino = childMap.get(b.childId);
              const nombre = nino?.nombre ?? `Niño ${b.childId.slice(0, 8)}`;
              const badge = ESTADO_BADGE[b.estado];
              const flags = b.flagsRevisarAudio + b.flagsDudoso;
              const firmada = firmaMap.get(b.id)?.firmada;
              return (
                <li key={b.id} className={s.row}>
                  <Link to={destino(b)} className={s.rowMain}>
                    <div className={s.rowTop}>
                      <strong>{nombre}</strong>
                      <span className={s.muted}>
                        {edadTexto(b.edadMeses)} · {new Date(b.createdAt).toLocaleDateString("es-PE")}
                      </span>
                    </div>
                    <div className={s.badges}>
                      <span className={s.banda}>Banda {b.banda}</span>
                      <span className={`${s.badge} ${badge.tono}`}>{badge.label}</span>
                      {b.modoPrueba ? <span className={s.qa}>QA</span> : null}
                      {b.profile ? (
                        <span className={s.perfil}>{PERFIL_CORTO[b.profile] ?? b.profile}</span>
                      ) : null}
                      {flags > 0 ? <span className={s.flags}>⚑ {flags}</span> : null}
                      {firmada ? <span className={s.firmada}>✔ firmada</span> : null}
                    </div>
                  </Link>
                  {b.estado === "completada" ? (
                    <div className={s.rowAccion}>
                      <Button
                        variant="primary"
                        disabled={procesandoId === b.id}
                        onClick={() => void procesar(b.id)}
                      >
                        {procesandoId === b.id ? "Procesando…" : "Procesar · 1 crédito"}
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {totalPaginas > 1 ? (
            <div className={s.pager}>
              <Button
                variant="ghost"
                disabled={offset <= 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
              >
                ‹ Anterior
              </Button>
              <span className={s.muted}>
                Página {pagina} de {totalPaginas}
              </span>
              <Button
                variant="ghost"
                disabled={offset + PAGE >= total}
                onClick={() => setOffset(offset + PAGE)}
              >
                Siguiente ›
              </Button>
            </div>
          ) : null}
        </Card>
      </Content>
    </>
  );
}
